import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const TRANSITION_MS = 300;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export interface TeamMemberModalMember {
  name: string;
  role: string;
  imageSrc: string;
  imagePosition?: string;
  /** i18n prefix for team.members.* → body */
  profileKey?: string;
}

export interface TeamMemberModalProps {
  open: boolean;
  member: TeamMemberModalMember | null;
  onClose: () => void;
}

export function TeamMemberModal({
  open,
  member,
  onClose,
}: TeamMemberModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [displayMember, setDisplayMember] = useState<TeamMemberModalMember | null>(null);
  const [entered, setEntered] = useState(false);

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (open && member) {
      setDisplayMember(member);
    }
  }, [open, member]);

  useEffect(() => {
    if (!open && displayMember) {
      setEntered(false);
      if (prefersReducedMotion()) {
        setDisplayMember(null);
        return;
      }
      const tId = window.setTimeout(() => setDisplayMember(null), TRANSITION_MS);
      return () => clearTimeout(tId);
    }
  }, [open, displayMember]);

  useEffect(() => {
    if (!displayMember) {
      setEntered(false);
      return;
    }
    if (prefersReducedMotion()) {
      setEntered(true);
      return;
    }
    setEntered(false);
    const outer = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(outer);
  }, [displayMember]);

  useEffect(() => {
    if (!displayMember) return;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleEscape);
    };
  }, [displayMember, handleEscape]);

  if (!displayMember) return <></>;

  const profileText = displayMember.profileKey
    ? t(`${displayMember.profileKey}.body`)
    : t("team.memberBioPlaceholder");

  const renderProfileContent = (text: string): React.ReactElement => {
    const expMarker = "\n\nОпит:\n";
    const servicesMarker = "\n\nУслуги:\n";
    const expIdx = text.indexOf(expMarker);
    const servicesIdx = text.indexOf(servicesMarker);

    if (expIdx === -1 || servicesIdx === -1 || servicesIdx <= expIdx) {
      return (
        <p className="text-gray-body text-base leading-relaxed text-left w-full whitespace-pre-line">
          {text}
        </p>
      );
    }

    const intro = text.slice(0, expIdx).trim();
    const expContent = text.slice(expIdx + expMarker.length, servicesIdx).trim();
    const servicesContent = text.slice(servicesIdx + servicesMarker.length).trim();

    const expBlocks = expContent
      .split("\n\n")
      .map((block) => block.trim())
      .filter(Boolean)
      .map((block) => {
        const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
        return {
          title: lines[0] ?? "",
          body: lines.slice(1).join(" "),
        };
      })
      .filter((block) => block.title && block.body);

    const services = servicesContent
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.startsWith("- "))
      .map((line) => line.replace(/^- /, "").trim())
      .filter(Boolean);

    return (
      <div className="text-gray-body text-base leading-relaxed text-left w-full space-y-4">
        {intro ? <p className="whitespace-pre-line">{intro}</p> : null}

        <div className="space-y-3">
          <p className="font-semibold text-navy">Опит</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {expBlocks.map((block) => (
              <div key={block.title} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="font-semibold text-navy mb-2">{block.title}</p>
                <p className="text-gray-body">{block.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-semibold text-navy">Услуги</p>
          <ul className="list-disc pl-6 space-y-1">
            {services.map((service) => (
              <li key={service}>{service}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 transition-opacity duration-300 ease-out motion-reduce:transition-none ${
        entered ? "opacity-100" : "opacity-0"
      }`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="team-member-modal-title"
    >
      <div
        className={`bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${
          entered
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-[0.97] translate-y-3 sm:translate-y-4"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl gap-3 z-10">
          <h2
            id="team-member-modal-title"
            className="text-xl font-semibold text-navy leading-tight pr-2"
          >
            {displayMember.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="relative shrink-0 w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-navy/20"
            aria-label={t("nav.close")}
          >
            <span className="sr-only">{t("nav.close")}</span>
            <span className="absolute w-5 h-0.5 bg-current rounded rotate-45" />
            <span className="absolute w-5 h-0.5 bg-current rounded -rotate-45" />
          </button>
        </div>

        <div className="p-6 flex flex-col items-center gap-6">
          <div className="w-40 h-40 rounded-full border-2 border-gold overflow-hidden shrink-0 bg-gray-200">
            <img
              src={displayMember.imageSrc}
              alt=""
              className="w-full h-full object-cover"
              style={
                displayMember.imagePosition
                  ? { objectPosition: displayMember.imagePosition }
                  : undefined
              }
            />
          </div>
          <p className="text-gray-body text-base font-medium text-center whitespace-pre-line leading-relaxed">
            {displayMember.role}
          </p>
          {renderProfileContent(profileText)}
        </div>
      </div>
    </div>
  );
}

export default TeamMemberModal;
